/**
 * Écran de retour — le **journal de la ville** (LIVRAISON étape 8, VISION 4.2).
 *
 * Pas une boîte comptable. « Vous avez gagné 1 234 pièces » est un relevé bancaire ;
 * ce que le joueur veut savoir, c'est **ce qui s'est passé chez lui pendant son
 * absence**. Le journal raconte donc la ville — elle a produit, elle a tourné, des gens
 * y ont vécu — et le chiffre arrive comme la conséquence, pas comme le sujet.
 *
 * Deux honnêtetés non négociables :
 *
 * · **le plafond se dit.** `offlineEarnings` rend `cappedMs` précisément pour ça. Taire
 *   les heures écartées ferait croire à un bug la première fois qu'un joueur revient
 *   après trois jours et retrouve la même somme qu'après huit heures ;
 * · **le rendement se dit aussi.** Le hors-ligne rapporte 25 % : l'annoncer transforme
 *   une frustration (« j'ai moins gagné que prévu ») en argument de revenir jouer.
 *
 * L'écran ne s'ouvre pas pour trois minutes d'absence : une modale qui s'interpose entre
 * le joueur et sa ville à chaque rechargement serait une taxe, pas une célébration.
 */

import { duration, n, t } from '@/i18n';
import { createButton } from './components';
import { createModal } from './components/overlays';
import { icon } from './icons';

/**
 * Absence minimale pour mériter un écran.
 *
 * Dix minutes : en dessous, le joueur sait très bien qu'il vient de recharger la page,
 * et l'écran se lirait comme un obstacle. Au-dessus, il a vécu autre chose entre-temps
 * et le retour est un moment.
 */
export const RETURN_SCREEN_MIN_MS = 10 * 60 * 1000;

export interface ReturnScreenCallbacks {
  /**
   * Rachète les heures écrêtées de CE retour (G§3, A4).
   *
   * Rend `true` si le rachat a eu lieu : l'écran se met alors à jour au lieu de se
   * fermer — le joueur doit VOIR ce qu'il vient d'acheter, sinon il aura payé pour un
   * nombre qu'il n'a jamais lu.
   */
  readonly onRecover?: () => boolean;
}

export interface ReturnScreenData {
  readonly coins: number;
  /** Durée réellement créditée, après plafond. */
  readonly creditedMs: number;
  /** Durée écartée par le plafond. Zéro si l'absence tenait dans la limite. */
  readonly cappedMs: number;
  /** Rendement hors-ligne appliqué, de 0 à 1 — affiché en clair. */
  readonly efficiency: number;
  /**
   * Gemmes demandées pour rattraper les heures écrêtées, `undefined` si l'offre ne
   * s'applique pas (rien d'écrêté, ou nuit blanche déjà consommée pour ce retour).
   *
   * Un nombre plutôt qu'un booléen : l'écran affiche le prix, et le lire depuis le
   * catalogue ici serait une deuxième source de vérité pour la même valeur.
   */
  readonly recoverCost?: number;
  /** La nuit blanche a été consommée pour ce retour — on le DIT, sinon le rendement surprend. */
  readonly nightUsed?: boolean;
}

export interface ReturnScreenHandle {
  /** Affiche le journal. Sans effet si l'absence est trop courte pour mériter un écran. */
  show(data: ReturnScreenData): boolean;
  /** Réécrit le total affiché — après un rachat d'heures écrêtées. */
  recredit(coins: number): void;
  destroy(): void;
}

export function createReturnScreen(
  onClose?: () => void,
  callbacks?: ReturnScreenCallbacks,
): ReturnScreenHandle {
  const modal = createModal({ title: t('offline.welcomeBack'), icon: 'coin' });
  document.body.append(modal.el);

  const lead = document.createElement('p');
  lead.className = 'return-screen__lead';

  const amount = document.createElement('div');
  amount.className = 'return-screen__amount';

  const notes = document.createElement('ul');
  notes.className = 'return-screen__notes';

  modal.content.append(lead, amount, notes);

  const close = createButton({
    label: t('offline.collect'),
    variant: 'primary',
    onClick: () => {
      modal.close();
      onClose?.();
    },
  });
  /*
    ⚠️ NOMMÉ, et pas seulement stylé (règle 22). Le parcours enchaîné visait « le premier
    `.v-btn` du scrim ouvert » : c'était « Encaisser » jusqu'à ce que le bouton de rachat
    se glisse devant lui — et le test a échoué sur « cible sans boîte englobante », un
    symptôme muet sur sa cause, parce que le rachat est masqué quand rien n'est écrêté.
    Exactement le défaut que la boutique avait déjà payé avec `.store-spend__row .v-btn`.
  */
  close.el.classList.add('return-screen__collect');

  /*
    ── LE MOMENT DE VENTE LE MIEUX PLACÉ DU JEU (G§7.2) ──────────────────────

    Cet écran dit déjà au joueur combien d'heures le plafond lui a coûté. Le rachat est
    donc la seule offre du jeu dont la valeur est **vérifiable par le joueur lui-même**
    avant de payer : il paie pour du temps qu'il a réellement accumulé, et le nombre est
    sous ses yeux.

    Le bouton est SECONDAIRE : « Encaisser » reste l'action principale. Une offre payante
    en bouton primaire sur un écran qui célèbre un gain gratuit transformerait la
    célébration en péage.
  */
  const recover = createButton({
    label: '',
    variant: 'secondary',
    icon: 'gem',
    onClick: () => {
      // L'écran ne se ferme pas : il se REDESSINE avec le nouveau total. Voir ce qu'on
      // vient d'acheter fait partie de l'achat (E§1.1 — on célèbre l'action).
      if (callbacks?.onRecover?.() === true) recover.el.hidden = true;
    },
  });
  recover.el.classList.add('return-screen__recover');
  recover.el.hidden = true;

  modal.actions.append(recover.el, close.el);

  function line(text: string): HTMLElement {
    const item = document.createElement('li');
    item.textContent = text;
    return item;
  }

  return {
    show(data) {
      if (data.creditedMs < RETURN_SCREEN_MIN_MS || data.coins <= 0) return false;

      // Le sujet, c'est la ville — pas le solde.
      lead.textContent = t('offline.lead', { duration: duration(data.creditedMs) });

      amount.replaceChildren(icon('coin', { size: 'large' }), `+${n(data.coins)}`);

      notes.replaceChildren();
      // Le rendement, annoncé : une différence expliquée n'est plus une déception.
      notes.append(line(t('offline.efficiency', { value: n(Math.round(data.efficiency * 100)) })));
      if (data.nightUsed === true) {
        // Une nuit blanche dépensée sans le dire, c'est un achat qui s'évapore.
        notes.append(line(t('offline.nightUsed')));
      }
      if (data.cappedMs > 0) {
        // Le plafond, annoncé aussi : sinon le joueur croit à un bug.
        notes.append(line(t('offline.capped', { duration: duration(data.cappedMs) })));
      }

      const cost = data.recoverCost;
      recover.el.hidden = cost === undefined || data.cappedMs <= 0;
      if (cost !== undefined) {
        recover.setLabel(
          t('offline.nightOffer', { duration: duration(data.cappedMs), value: n(cost) }),
        );
      }

      modal.open();
      return true;
    },
    /**
     * Réécrit le montant après un rachat, sans rouvrir la modale.
     *
     * Sans cette méthode, le joueur paierait 25 gemmes en regardant l'ancien total : le
     * seul retour serait la disparition du bouton, ce qui se lit comme une panne.
     */
    recredit(coins) {
      amount.replaceChildren(icon('coin', { size: 'large' }), `+${n(coins)}`);
    },
    destroy() {
      modal.destroy();
    },
  };
}
